/**
 * Superadmin CRUD over the consent list users are prompted with.
 *
 * Reads GET /api/consents (superadmin-only; a 403 means "not an admin").
 * Mutations refresh silently and resolve once the refreshed list has landed,
 * same as use_person_field_admin.js. Clears the consent gate's cache so the
 * admin's own session re-checks after a change.
 *
 *   const { consents, loading, create, update, remove } = useConsentAdmin();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';
import { invalidatePendingConsents } from './use_pending_consents';

export function useConsentAdmin() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [consents, setConsents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(({ silent = false } = {}) => {
        if (!userId) { setLoading(false); return Promise.resolve(); }
        if (!silent) setLoading(true);
        return api.get('/api/consents', { params: { user_id: userId } })
            .then(data => { setConsents(Array.isArray(data) ? data : []); setError(null); })
            .catch(err => setError(err))
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const after = useCallback(result => {
        invalidatePendingConsents();
        return load({ silent: true }).then(() => result);
    }, [load]);

    const create = useCallback(payload => api.post('/api/consents', payload, { params: { user_id: userId } })
        .then(after), [api, userId, after]);

    const update = useCallback((id, payload) => api.put(`/api/consents/${id}`, payload, { params: { user_id: userId } })
        .then(after), [api, userId, after]);

    const remove = useCallback(id => api.del(`/api/consents/${id}`, { params: { user_id: userId } })
        .then(after), [api, userId, after]);

    return { consents, loading, error, refresh: load, create, update, remove };
}

export default useConsentAdmin;
